import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneRight, MapPin, Phone, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { PRACTICE_AREAS, FIRM } from "../lib/practiceAreas";
import { scrollToId } from "../hooks/useLenis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  practice_area: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Please complete your name, email, and a brief message (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, practice_area: form.practice_area || null, phone: form.phone || null };
      const res = await axios.post(`${API}/contact`, payload);
      if (res.data?.email_sent) {
        toast.success("Thank you — your enquiry has been sent. We'll respond shortly.");
      } else {
        toast.success("Thank you — your enquiry has been received. We'll be in touch soon.");
      }
      setForm(initialState);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0B0B0E] border-t border-white/5 section-light"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left intro + info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#E9C76A]" />
            <span className="overline">Get in touch</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F5F2E8]">
            Tell us about your <span className="italic text-[#E9C76A]">matter</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/65 font-light leading-relaxed">
            Every enquiry is reviewed by a partner. Share a short summary and we&apos;ll
            respond within one working day.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <MapPin size={22} weight="thin" className="text-[#E9C76A] mt-1 flex-shrink-0" />
              <div>
                <div className="overline mb-1">Registered Office</div>
                <div className="text-white/80 font-light">{FIRM.address}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={22} weight="thin" className="text-[#E9C76A] mt-1 flex-shrink-0" />
              <div>
                <div className="overline mb-1">Telephone</div>
                <a
                  data-testid="contact-phone-link"
                  href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
                  className="text-white/80 hover:text-[#E9C76A] transition-colors font-light"
                >
                  {FIRM.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <EnvelopeSimple size={22} weight="thin" className="text-[#E9C76A] mt-1 flex-shrink-0" />
              <div>
                <div className="overline mb-1">General Enquiries</div>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${FIRM.email}`}
                  className="text-white/80 hover:text-[#E9C76A] transition-colors font-light"
                >
                  {FIRM.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock size={22} weight="thin" className="text-[#E9C76A] mt-1 flex-shrink-0" />
              <div>
                <div className="overline mb-1">Free Walk-In</div>
                <div className="text-white/80 font-light">{FIRM.walkInHours}</div>
              </div>
            </div>
          </div>

          {/* Google Maps embed */}
          <div className="mt-10 border border-white/10 overflow-hidden" data-testid="contact-map">
            <iframe
              title="Lawfields Solicitors office location"
              src="https://www.google.com/maps?q=47+North+Road,+Southall,+Middlesex+UB1+2JL,+United+Kingdom&output=embed"
              width="100%"
              height="280"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
              allowFullScreen
            />
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=47+North+Road,+Southall,+Middlesex+UB1+2JL"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-directions-link"
              className="flex items-center justify-between px-5 py-4 bg-[#141414] hover:bg-[#1a1a1a] transition-colors text-xs uppercase tracking-[0.2em] text-[#E9C76A]"
            >
              <span>Get directions</span>
              <span className="text-white/40">→</span>
            </a>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onSubmit={onSubmit}
          noValidate
          data-testid="contact-form"
          className="lg:col-span-7 border border-white/10 bg-[#14141A] p-8 md:p-12 glass-card"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="overline mb-3 block !text-white/40">Full Name</label>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                className="lawfields-input w-full"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="overline mb-3 block !text-white/40">Email</label>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                className="lawfields-input w-full"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="overline mb-3 block !text-white/40">Phone (optional)</label>
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                className="lawfields-input w-full"
                placeholder="+44 7XXX XXXXXX"
              />
            </div>
            <div>
              <label className="overline mb-3 block !text-white/40">Practice Area</label>
              <Select
                value={form.practice_area}
                onValueChange={(v) => onChange("practice_area", v)}
              >
                <SelectTrigger
                  data-testid="contact-select-area"
                  className="lawfields-input w-full text-left"
                >
                  <SelectValue placeholder="Select an area" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border border-white/10 text-[#F5F2E8] rounded-none">
                  {PRACTICE_AREAS.map((a) => (
                    <SelectItem
                      key={a.id}
                      value={a.title}
                      data-testid={`contact-area-option-${a.id}`}
                      className="font-sans text-sm focus:bg-[#E9C76A]/10 focus:text-[#E9C76A] rounded-none"
                    >
                      {a.title}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="General Enquiry"
                    className="font-sans text-sm focus:bg-[#E9C76A]/10 focus:text-[#E9C76A] rounded-none"
                  >
                    General Enquiry
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <label className="overline mb-3 block !text-white/40">How can we help?</label>
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => onChange("message", e.target.value)}
              rows={6}
              className="lawfields-input w-full resize-none"
              placeholder="A brief outline of your matter..."
            />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <p className="text-xs text-white/40 font-light max-w-md leading-relaxed">
              By submitting this form you consent to Lawfields contacting you about your enquiry.
              We do not share your details with third parties.
            </p>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={submitting}
              className="btn-gold inline-flex items-center gap-3"
            >
              {submitting ? "Sending..." : "Send Enquiry"}
              <PaperPlaneRight size={14} weight="bold" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
